// Worker page section: alerts (platform.alert_state) plus the assertions whose
// last evaluation errored (platform.monitor_runs). Read-only (SQ-14).
import { useState } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import { useWorkerAlerts, useWorkerMonitorRuns } from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import { QueryState, SectionHeading, SeverityBadge, fmtTs } from '../worker-ui';

function AlertsSection() {
  const [state, setState] = useState<'open' | 'all'>('open');
  const q = useWorkerAlerts(state);
  const runs = useWorkerMonitorRuns();
  const rows = q.data?.alerts ?? [];
  const errored = (runs.data?.runs ?? []).filter((r) => r.lastStatus === 'error');
  return (
    <section className="flex flex-col gap-3" aria-label="Alerts">
      <SectionHeading
        title="Alerts"
        description="Monitor alert state. Open alerts re-notify every 6 hours; drills open their own drill: keys."
      />
      <div className="flex items-center gap-2" role="group" aria-label="Alert state">
        {(['open', 'all'] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={state === s ? 'emphasis' : 'secondary'}
            aria-pressed={state === s}
            onClick={() => setState(s)}
          >
            {s === 'open' ? 'Open' : 'All'}
          </Button>
        ))}
      </div>
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={rows.length === 0}
        emptyTitle={state === 'open' ? 'No open alerts' : 'No alerts'}
        onRetry={() => void q.refetch()}
      >
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Alert</TableHeaderCell>
                <TableHeaderCell>Severity</TableHeaderCell>
                <TableHeaderCell>Subjects</TableHeaderCell>
                <TableHeaderCell>Opened</TableHeaderCell>
                <TableHeaderCell>Last notified</TableHeaderCell>
                <TableHeaderCell>Resolved</TableHeaderCell>
                <TableHeaderCell>Owner</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.key}>
                  <TableCell>
                    <div className="text-body-sm-emphasis">{a.key}</div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {a.drill && <Badge variant="info">drill</Badge>}
                      {a.money && <Badge variant="info">money</Badge>}
                      {a.truncated && <Badge variant="warning">truncated</Badge>}
                      {a.error && (
                        <span className="text-caption text-danger-text">
                          {[a.error.sqlstate, a.error.message].filter(Boolean).join(': ')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <SeverityBadge severity={a.severity} />
                  </TableCell>
                  <TableCell>
                    <div>{a.subjectsOpen ?? '—'}</div>
                    {a.sampleSubjects.length > 0 && (
                      <div className="text-caption text-muted">{a.sampleSubjects.join(', ')}</div>
                    )}
                  </TableCell>
                  <TableCell>{fmtTs(a.openedAt)}</TableCell>
                  <TableCell>{fmtTs(a.lastNotifiedAt)}</TableCell>
                  <TableCell>{fmtTs(a.resolvedAt)}</TableCell>
                  <TableCell>{a.owner ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
      {errored.length > 0 && (
        <div className="flex flex-col gap-1" aria-label="Assertions whose last run errored">
          <h3 className="text-body-sm-emphasis text-primary">Last evaluation errored</h3>
          <ul className="text-body-sm">
            {errored.map((r) => (
              <li key={r.assertion}>
                {r.assertion} (last run {fmtTs(r.lastRunAt)}, last ok {fmtTs(r.lastOkAt)})
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'alerts',
  order: 40,
  label: 'Alerts',
  perm: PERMS.WORKER_READ,
  Component: AlertsSection,
};
